// Frontend/src/components/admin/forms/MinistryForm.jsx
import { useEffect, useState } from 'react';
import { STORAGE_PATHS } from '../../../constants/storagePaths';
import { useLeaders } from '../../../contexts/LeaderContext';
import { useMinistries } from '../../../contexts/MinistryContext';
import { uploadFile } from '../../../services/uploadFile';
import AutocompleteMultiSelect from '../ui/AutocompleteMultiSelect';
import FileUpload from '../ui/FileUpload';
import Input from '../ui/Input';

const MinistryForm = ({ initialValues, onSubmit, onCancel, isSubmitting }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cover_image: '',
    logo_image: '',
    meeting_day: '',
    meeting_time: '',
    location: '',
    leaders: [], // Array of leader IDs
    status: 'active',
    ...initialValues
  });

  const [logoPreview, setLogoPreview] = useState(initialValues?.logo_image || '');
  const [coverPreview, setCoverPreview] = useState(initialValues?.cover_image || '');
  const [uploadErrors, setUploadErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState({});

  const { leaders } = useLeaders();
  const { setError } = useMinistries();

  // Convert leaders to options format for the autocomplete
  const leaderOptions = leaders.map(leader => ({
    id: leader.id,
    value: leader.id,
    label: leader.name || `${leader.firstName} ${leader.lastName}` || 'Unnamed Leader',
    fullName: leader.name || `${leader.firstName} ${leader.lastName}`,
    email: leader.email,
    phone: leader.phone
  }));

  // Update form when initialValues change
  useEffect(() => {
    if (initialValues) {
      // Get the current ministry leaders from the leaders context
      const ministryLeaders = leaders
        .filter(leader => 
          leader.leaderType?.includes("ministry") && 
          leader.ministryId === initialValues.id
        )
        .map(leader => leader.id);

      setFormData(prev => ({
        ...prev,
        ...initialValues,
        leaders: ministryLeaders // Set the actual leader IDs
      }));
      
      setLogoPreview(initialValues.logo_image || '');
      setCoverPreview(initialValues.cover_image || '');
    }
  }, [initialValues, leaders]);

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Ministry name is required';
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }

    if (formData.leaders.length === 0) {
      errors.leaders = 'At least one leader must be assigned';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleLeadersChange = (selectedLeaderIds) => {
    setFormData(prev => ({ ...prev, leaders: selectedLeaderIds }));
    
    // Clear validation error when leaders are selected
    if (validationErrors.leaders) {
      setValidationErrors(prev => ({ ...prev, leaders: '' }));
    }
  };

  const handleLogoUpload = async (file) => {
    try {
      setUploadErrors(prev => ({ ...prev, logo: null }));
      
      const logo_image = await uploadFile(file, STORAGE_PATHS.LOGOS);
      setFormData(prev => ({ ...prev, logo_image }));
      setLogoPreview(URL.createObjectURL(file));
      
    } catch (error) {
      console.error('Failed to upload logo:', error);
      setUploadErrors(prev => ({ 
        ...prev, 
        logo: error.response?.data?.message || error.message || 'Failed to upload logo'
      }));
    }
  };

  const handleCoverUpload = async (file) => {
    try {
      setUploadErrors(prev => ({ ...prev, cover: null }));
      
      const cover_image = await uploadFile(file, STORAGE_PATHS.LOGOS);
      setFormData(prev => ({ ...prev, cover_image }));
      setCoverPreview(URL.createObjectURL(file));
      
    } catch (error) {
      console.error('Failed to upload cover:', error);
      setUploadErrors(prev => ({ 
        ...prev, 
        cover: error.response?.data?.message || error.message || 'Failed to upload cover image'
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      // Prepare the form data for submission
      const submitData = {
        ...formData,
        // Ensure required fields are properly formatted
        name: formData.name.trim(),
        description: formData.description.trim(),
        meeting_day: formData.meeting_day?.trim() || null,
        meeting_time: formData.meeting_time?.trim() || null,
        location: formData.location?.trim() || null,
        status: formData.status || 'active'
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
      
      // Handle validation errors from backend
      if (error.response?.status === 400 && error.response?.data?.errors) {
        setValidationErrors(error.response.data.errors);
      } else {
        // Try to set error in context if available, otherwise use local state
        if (setError) {
          setError({
            message: error.response?.data?.message || 'Failed to save ministry. Please try again.'
          });
        } else {
          // Fallback: show validation error
          setValidationErrors({
            general: error.response?.data?.message || 'Failed to save ministry. Please try again.'
          });
        }
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* General Error Display */}
      {validationErrors.general && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
              <svg className="w-4 h-4 text-red-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{validationErrors.general}</p>
            </div>
          </div>
        </div>
      )}

      <Input
        label="Ministry Name"
        name="name"
        value={formData.name}
        onChange={handleChange}
        required
        placeholder="Enter ministry name..."
        error={validationErrors.name}
      />
      
      <div>
        <AutocompleteMultiSelect
          label="Ministry Leaders"
          options={leaderOptions}
          selectedValues={formData.leaders}
          onChange={handleLeadersChange}
          placeholder="Search and select leaders..."
          required
          searchKey="fullName"
          displayKey="label"
        />
        {validationErrors.leaders && (
          <p className="text-red-500 text-sm mt-1">{validationErrors.leaders}</p>
        )}
      </div>
      
      <Input
        label="Description"
        name="description"
        value={formData.description}
        onChange={handleChange}
        type="textarea"
        rows={4}
        placeholder="Describe the ministry's purpose and activities..."
        required
        error={validationErrors.description}
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Meeting Day"
          name="meeting_day"
          value={formData.meeting_day}
          onChange={handleChange}
          placeholder="e.g., Sunday, Wednesday"
        />
        
        <Input
          label="Meeting Time"
          name="meeting_time"
          value={formData.meeting_time}
          onChange={handleChange}
          placeholder="e.g., 10:00 AM"
        />
        
        <Input
          label="Location"
          name="location"
          value={formData.location}
          onChange={handleChange}
          placeholder="e.g., Main Hall, Room 201"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          type="select"
          options={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'pending', label: 'Pending' }
          ]}
          required
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <FileUpload
            label="Ministry Logo"
            onFileChange={handleLogoUpload}
            preview={logoPreview}
            accept="image/*"
          />
          {uploadErrors.logo && (
            <p className="text-red-500 text-sm mt-1">{uploadErrors.logo}</p>
          )}
        </div>
        
        <div>
          <FileUpload
            label="Cover Image"
            onFileChange={handleCoverUpload}
            preview={coverPreview}
            accept="image/*"
          />
          {uploadErrors.cover && (
            <p className="text-red-500 text-sm mt-1">{uploadErrors.cover}</p>
          )}
        </div>
      </div>
      
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : (initialValues?.id ? 'Update Ministry' : 'Create Ministry')}
        </button>
      </div>
    </form>
  );
};

export default MinistryForm;